import { calculateFees } from '@/apis/payments/requests'
import { Button } from '@/app/common/components/Button'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import MultiQueryPageLoader from '@/app/common/components/PageLoader/MultiQueryPageLoader'
import { getTotalAmountToBePaidByApplicant } from '@/app/common/payment.utils'
import { currencyFormatter } from '@/app/common/utilities'
import { Provider } from '@/schemas/provider.schema'
import { Label } from '@/shad/ui/label'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { useQuery } from '@tanstack/react-query'
import { t } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import FamilyWizardLayout from '../../family_application/FamilyWizardLayout'
import { SubmitButton } from '../../family_application/student_details/shared/submit-button'
import { useStudentPurses } from '../api/usePaymentProviders'
import { AddDetailsToOrderForm } from '../schema/providers.schema'
import PaymentFeesResultsCard from './PaymentFeesResultsCard'

type Props = {
  defaultTotal: number
  provider: Provider
  onNext: (details: AddDetailsToOrderForm) => void
  onBack: () => void
}

export const AddDetails = (props: Props) => {
  const { defaultTotal, onNext, provider, onBack } = props

  const { t: translate } = useTranslation()
  const application = useSelector(selectFamilyApplication)
  const programId = useSelector(selectProgramID)

  const studentPursesQuery = useStudentPurses({
    application_id: application.application_id,
    program_id: programId,
  })

  const isValidCalculation = !isNaN(defaultTotal) && defaultTotal > 0
  const calculateFeesQuery = useQuery({
    queryFn: async () =>
      await calculateFees({
        base_amount: defaultTotal,
        program_id: programId,
      }),
    queryKey: ['calculateFees', defaultTotal, programId],
    enabled: isValidCalculation,
  })

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('payment_details.title')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-full">
        <MultiQueryPageLoader queries={[studentPursesQuery] as const}>
          {([studentPurses]) => {
            const maxAmount = studentPurses.reduce((acc, curr) => {
              return acc + (curr.balance_available || 0)
            }, 0)

            const isAboveMaxAmount = (() => {
              if (!calculateFeesQuery?.data) return false
              const totalAmount = getTotalAmountToBePaidByApplicant(
                calculateFeesQuery.data
              )
              return totalAmount > maxAmount
            })()

            return (
              <div>
                <Card className="w-full">
                  <CardHeader>
                    <h2 className="flex text-base w-full text-left font-medium text-gray-800">
                      {translate('order_details')}
                    </h2>
                    <p className="flex w-full text-sm font-normal text-gray-600">
                      {provider.name}
                    </p>
                  </CardHeader>
                  <CardContent className="w-full">
                    <div className="space-y-3">
                      <section
                        className="flex flex-col"
                        aria-labelledby="payment_amount_label"
                      >
                        <Label id="payment_amount_label">
                          {translate('payment_amount')}
                        </Label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                          {currencyFormatter.format(defaultTotal)}
                        </p>
                        {isAboveMaxAmount && (
                          <FormErrorMessage
                            message={t('you_dont_have_enough_funds_to_cover')}
                          />
                        )}
                        <div className="text-sm text-gray-600 mt-2">
                          {t('total_available')}:{' '}
                          {currencyFormatter.format(maxAmount)}
                        </div>
                      </section>
                      <div className="mt-6">
                        <PaymentFeesResultsCard
                          isEnabled={isValidCalculation}
                          paymentFeesQuery={calculateFeesQuery}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <div className="flex gap-2 mt-10">
                  <Button
                    variant="outline"
                    onClick={onBack}
                    className="px-10"
                  >
                    {translate('back')}
                  </Button>
                  <SubmitButton
                    forceDisabled={
                      isAboveMaxAmount || calculateFeesQuery.isFetching
                    }
                    onClick={() => onNext({ payment_amount: defaultTotal })}
                    status="idle"
                    buttonTxt={translate('next')}
                    className="px-10"
                  />
                </div>
              </div>
            )
          }}
        </MultiQueryPageLoader>
      </FamilyWizardLayout.Content>
    </FamilyWizardLayout>
  )
}
